import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
`;

const Title = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.35rem;
  color: #fff;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.85rem;
    color: #a0a0a0;
    margin-bottom: 0.35rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(15, 15, 35, 0.9);
    color: #fff;
    font-size: 0.9rem;
  }

  textarea {
    min-height: 72px;
    resize: vertical;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const Btn = styled.button`
  padding: 0.65rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  color: #fff;

  &.ghost {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  &.primary {
    background: linear-gradient(135deg, #4facfe, #00f2fe);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
`;

function AssetEditModal({ asset, onClose, onSave, busy }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    valuationUSD: '',
    location: '',
    ownerAddress: '',
    custodian: '',
    country: ''
  });

  useEffect(() => {
    if (!asset) {
      return;
    }

    setForm({
      name: asset.name || '',
      description: asset.description || '',
      valuationUSD: asset.valuationUSD != null ? String(asset.valuationUSD) : '',
      location: asset.location || '',
      ownerAddress: asset.ownerAddress || '',
      custodian: asset.custodian || '',
      country: asset.country || ''
    });
  }, [asset]);

  if (!asset) {
    return null;
  }

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(asset.id, {
        name: form.name.trim(),
        description: form.description.trim(),
        valuationUSD: parseFloat(form.valuationUSD) || 0,
        location: form.location.trim(),
        ownerAddress: form.ownerAddress.trim(),
        custodian: form.custodian.trim(),
        country: form.country.trim() || undefined
      });
      onClose();
    } catch (err) {
      /* Parent / API layer surfaces errors */
    }
  };

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(ev) => ev.target === ev.currentTarget && onClose()}
    >
      <Modal
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        style={{ position: 'relative' }}
        onClick={(ev) => ev.stopPropagation()}
      >
        <CloseBtn type="button" onClick={onClose} aria-label="Close">
          <FiX />
        </CloseBtn>
        <Title>Edit asset (PUT /api/assets/{"{id}"})</Title>
        <form onSubmit={handleSubmit}>
          <Field>
            <label>Title</label>
            <input value={form.name} onChange={(e) => update('name', e.target.value)} required />
          </Field>
          <Field>
            <label>Description</label>
            <textarea value={form.description} onChange={(e) => update('description', e.target.value)} />
          </Field>
          <Field>
            <label>Valuation (USD)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.valuationUSD}
              onChange={(e) => update('valuationUSD', e.target.value)}
            />
          </Field>
          <Field>
            <label>Location</label>
            <input value={form.location} onChange={(e) => update('location', e.target.value)} />
          </Field>
          <Field>
            <label>Owner wallet / identifier</label>
            <input value={form.ownerAddress} onChange={(e) => update('ownerAddress', e.target.value)} />
          </Field>
          <Field>
            <label>Custodian / current owner name</label>
            <input value={form.custodian} onChange={(e) => update('custodian', e.target.value)} />
          </Field>
          <Field>
            <label>Country</label>
            <input value={form.country} onChange={(e) => update('country', e.target.value)} />
          </Field>
          <Actions>
            <Btn type="button" className="ghost" onClick={onClose}>
              Cancel
            </Btn>
            <Btn type="submit" className="primary" disabled={busy}>
              Save
            </Btn>
          </Actions>
        </form>
      </Modal>
    </Overlay>
  );
}

export default AssetEditModal;
